import {useState, useEffect} from 'react'
import Sticky from 'react-stickynode'
import type {JSX} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block} from '@/services/kontent-ai/types'
import {useWindowSize} from '@/utilities/hooks/use-window-size'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'

export type StickyBarContentItem = IContentItem<{
	brand: Elements.MultipleChoiceElement
	brandColor: Elements.CustomElement
}>

const background = {
	primary: 'bg-theme-main',
	secondary: 'bg-blue-900',
	galliprant: 'bg-galliprant',
	trifexis: 'bg-trifexis',
	micotil: 'bg-micotil',
	// CMS dropdown uses 'interceptorplus' (no underscore); registry ID is 'interceptor_plus'
	interceptorplus: 'bg-interceptor_plus',
	capstar: 'bg-capstar',
	credelio: 'bg-credelio',
	bewormready: 'bg-bewormready',
	entyce: 'bg-entyce',
	elura: 'bg-elura',
	comfortis: 'bg-comfortis',
	advocate: 'bg-advocate',
	// CMS dropdown uses 'experior_green'; registry only has 'experior'
	experior_green: 'bg-experior',
	hemicell: 'bg-hemicell',
}

const Bar = ({
	brand = 'primary',
	expanded = false,
	fixed = false,
	id = '',
	className = '',
}: {
	/** Legacy brand codename or any registry color ID (e.g. 'seresto') */
	brand?: string
	expanded?: boolean
	fixed?: boolean
	id?: string
	className?: string
}): JSX.Element => {
	let height = ''
	if (expanded) {
		height = 'h-12'
	} else {
		height = fixed ? 'h-1 md:h-2' : 'h-8'
	}

	// Legacy brands use the pre-defined Tailwind class map; new registry colors
	// use a dynamic bg-{colorId} class (safelisted via tailwind.config.js).
	const bgClass =
		(background as Record<string, string | undefined>)[brand] ??
		`bg-${brand}`

	return (
		<hr
			className={`${className} border-0 transition-all duration-200 ease-out ${height} ${bgClass} ${
				fixed
					? 'after:content-[" "] -z-1 md:absolute md:bottom-0 md:inline-block md:h-1 md:w-full md:shadow-secondary'
					: ''
			}`}
			id={id}
		/>
	)
}

export const StickyBarBlock: Block<StickyBarContentItem> = ({block, page}) => {
	const [topOffset, setTopOffset] = useState(0)

	const size = useWindowSize()

	useEffect(() => {
		const secondaryHeader = document.querySelector(
			'[data-secondary-header]'
		)
		const hasSecondaryHeader =
			secondaryHeader &&
			secondaryHeader instanceof HTMLElement &&
			!size.isMobile
		const newTopOffset = hasSecondaryHeader
			? 48 + secondaryHeader.scrollHeight
			: 48
		if (newTopOffset !== topOffset) {
			setTopOffset(newTopOffset)
		}
	}, [size.isMobile, topOffset])

	// Color priority: 1) new brand color picker  2) legacy brand dropdown
	//                 3) page-level new color    4) page-level legacy color
	//                 5) 'primary' hardcoded fallback
	const {colorId} = colorWithFallback({
		blockColor: block.elements.brandColor,
		blockColorLegacy: block.elements.brand,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	const selectedBrandOrFallback = colorId ?? 'primary'

	return (
		<div data-kontent-element-codename={block.system.codename}>
			<Sticky bottomBoundary="#bottom-bar" innerZ="1" top={topOffset}>
				{({status}) => (
					<Bar
						brand={selectedBrandOrFallback}
						fixed={status === Sticky.STATUS_FIXED}
					/>
				)}
			</Sticky>
		</div>
	)
}
